import React, { memo, useCallback } from 'react';
import type { Node, NodeProps } from '@xyflow/react';
import { WorkflowNode } from './node';
import type { WorkflowNodeViewData } from './node';
import {
  resolveGraphNodeRenderableNodeByViewData,
  useWorkflowRenderCatalog,
} from '../workflow-render-catalog';
import { useWorkflowGraphNodeViewModel } from '../use-workflow-graph-node-view-model';
import { ModelScopeFactory } from '@cotera/client/v0/actions/framework';
import { WORKFLOW_SELECTED_CLASS } from './node-selection';
import {
  useNodeRefIssues,
  WorkflowNodeRefIssueMark,
} from './workflow-ref-issues-context';
import {
  useNodeStructureIssues,
  WorkflowNodeStructureIssueMark,
} from './workflow-structure-issues-context';

export type WorkflowFlowNodeData = {
  node: WorkflowNodeViewData;
};

export const WORKFLOW_FLOW_NODE_TYPE = 'workflowNode' as const;

export const WorkflowFlowNode = memo(function WorkflowFlowNode({
  data,
  selected,
}: NodeProps<Node<WorkflowFlowNodeData>>) {
  const nodeData = data as WorkflowFlowNodeData;
  const catalog = useWorkflowRenderCatalog();
  const entry = resolveGraphNodeRenderableNodeByViewData(
    catalog,
    nodeData.node
  );
  const viewModel = useWorkflowGraphNodeViewModel(nodeData.node);

  const className = selected === true ? WORKFLOW_SELECTED_CLASS : undefined;

  const flowProps = {
    node: nodeData.node,
    selected,
    className,
    viewModel,
  };

  const card =
    entry.renderFlowNode !== undefined ? (
      entry.renderFlowNode(flowProps)
    ) : (
      <WorkflowNode node={nodeData.node} className={className} />
    );

  const affordances = entry.renderFlowAffordances?.(flowProps) ?? null;
  // Beside the affordances rather than inside the card: the card's body is the
  // node type's to lay out, and this has to appear on every type the same way.
  const refIssues = useNodeRefIssues(nodeData.node.id);
  const structureIssues = useNodeStructureIssues(nodeData.node.id);
  // Register the per-node model via ModelScopeFactory, keyed on the node's
  // stable model id. The factory registers inside a deps-compared effect, so an
  // unstable `viewModel` object (a fresh literal each render) no longer
  // re-registers every render — which churned add/removeModel → notify() →
  // infinite re-render.
  const createModels = useCallback(
    () => (viewModel !== null ? [viewModel.model] : []),
    [viewModel]
  );

  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[viewModel?.model.id ?? null]}
    >
      <div className="group relative w-fit">
        {card}
        <div className="absolute -right-2 -top-2 flex items-center gap-1">
          <WorkflowNodeRefIssueMark issues={refIssues} />
          <WorkflowNodeStructureIssueMark messages={structureIssues} />
          {affordances}
        </div>
      </div>
    </ModelScopeFactory>
  );
});
